import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ShotsHeader } from "@/components/dashboard/profile/portfolio/shots/ShotsHeader";
import AddPortfolioItemForm from "@/components/dashboard/profile/portfolio/AddPortfolioItemForm";
import { useToast } from "@/hooks/use-toast";
import { PortfolioItem } from "@/hooks/profile";
import CustomLayout from "@/components/layout/CustomLayout";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { usePortfolioAutosave } from "@/hooks/domains/portfolio/usePortfolioAutosave";
import { PortfolioService } from "@/services/supabase/portfolio";
import PortfolioItemModal from "@/components/profile/PortfolioItemModal";
import { toast as sonnerToast } from "sonner";
import { useShotHistory, restoreFromSnapshot } from "@/hooks/domains/portfolio/useShotHistory";
import { useShotVersionHistory } from "@/hooks/domains/portfolio/useShotVersionHistory";
import { ShotVersion } from "@/services/supabase/shotVersions";
import { SaveVersionDialog } from "@/components/dashboard/profile/portfolio/SaveVersionDialog";
import { RestoreVersionDialog } from "@/components/dashboard/profile/portfolio/RestoreVersionDialog";
import { formatDistanceToNow } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ShotEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [savingAsDraft, setSavingAsDraft] = useState(false);
  const [saveTrigger, setSaveTrigger] = useState<{ asDraft: boolean } | null>(null);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [isLoadingShot, setIsLoadingShot] = useState(Boolean(id));
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewShot, setPreviewShot] = useState<PortfolioItem | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [showSaveVersionDialog, setShowSaveVersionDialog] = useState(false);
  const [showRestoreVersionDialog, setShowRestoreVersionDialog] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState<ShotVersion | null>(null);

  // Check if we have imported data from navigation state
  const importedData = location.state?.importedData as Partial<PortfolioItem> | undefined;

  // Create initial shot data
  const createInitialShot = (): PortfolioItem => ({
    id: id || crypto.randomUUID(), // Use URL ID if editing, or generate new UUID
    title: importedData?.title || "",
    description: importedData?.description || "",
    image_url: importedData?.image_url || "",
    images: importedData?.images || [],
    created_at: importedData?.created_at || new Date().toISOString(),
    status: importedData?.status || "draft",
  });

  const [shot, originalSetShot] = useState<PortfolioItem>(createInitialShot());

  // History management for undo/redo
  const {
    undo: performUndo,
    redo: performRedo,
    canUndo,
    canRedo,
    wrapSetState,
    clearHistory,
  } = useShotHistory(shot, { enabled: true, debounceMs: 500 });

  // Wrap setShot to track history
  const setShot = wrapSetState(originalSetShot);

  // Version history management
  const {
    versions,
    isLoading: isLoadingVersions,
    isSaving: isSavingVersion,
    loadVersions,
    createVersion,
    getVersion,
  } = useShotVersionHistory({ shotId: id, profileId: user?.id });

  // Load versions when shot ID is available
  useEffect(() => {
    if (id && user?.id) {
      loadVersions();
    }
  }, [id, user?.id, loadVersions]);

  // Undo handler - restores from history snapshot
  const handleUndo = () => {
    if (!canUndo || isSaving) return;
    const snapshot = performUndo();
    if (snapshot) {
      const restoredShot = restoreFromSnapshot(shot, snapshot);
      originalSetShot(restoredShot); // Use original to skip history tracking
    }
  };

  // Redo handler - restores from history snapshot
  const handleRedo = () => {
    if (!canRedo || isSaving) return;
    const snapshot = performRedo();
    if (snapshot) {
      const restoredShot = restoreFromSnapshot(shot, snapshot);
      originalSetShot(restoredShot); // Use original to skip history tracking
    }
  };

  // Autosave hook
  const hasMeaningfulContent = Boolean(
    shot.title?.trim() ||
    shot.description?.trim() ||
    shot.images?.length
  );

  const autosaveEnabled = hasMeaningfulContent && !isLoadingShot;

  const { saveStatus, currentItemId, isSaving: isAutosaving } = usePortfolioAutosave({
    portfolioItem: shot,
    itemId: id || undefined,
    debounceMs: 2000,
    enabled: autosaveEnabled,
    isManualSaving: isSaving, // Disable autosave when manual save is in progress
    onSaveSuccess: (savedShot) => {
      // Don't overwrite user's current input - only update metadata fields
      // This prevents race condition where saved data overwrites newer user input
      setShot(prev => ({
        ...prev,
        id: savedShot.id,
        created_at: savedShot.created_at,
        updated_at: savedShot.updated_at,
        profile_id: savedShot.profile_id,
        // Keep user's current form data for all editable fields
      }));
    },
    onSaveError: (error) => {
      console.error('[ShotEditorPage] Autosave failed:', error);
      sonnerToast.error('Failed to auto-save your changes. Please try again.');
    },
  });

  // Update URL when shot is first created (without page reload)
  useEffect(() => {
    if (currentItemId && !id) {
      // Replace URL without page reload
      window.history.replaceState(null, '', `/dashboard/portfolio/shot/${currentItemId}`);
    }
  }, [currentItemId, id]);

  // Keyboard shortcuts for undo/redo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is typing in an input or textarea
      const target = e.target as HTMLElement;
      const isEditable =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable ||
        target.closest('[contenteditable="true"]');

      if (isEditable || isSaving) return;

      // Detect platform (Mac uses metaKey, Windows/Linux uses ctrlKey)
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;

      // Undo: Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
      if (modKey && e.key === 'z' && !e.shiftKey) {
        if (canUndo) {
          e.preventDefault();
          handleUndo();
        }
      }

      // Redo: Cmd+Shift+Z (Mac) or Ctrl+Shift+Z / Ctrl+Y (Windows/Linux)
      if (
        (modKey && e.shiftKey && e.key === 'z') ||
        (modKey && e.key === 'y' && !isMac)
      ) {
        if (canRedo) {
          e.preventDefault();
          handleRedo();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [canUndo, canRedo, handleUndo, handleRedo, isSaving]);

  // Load existing shot when editing
  useEffect(() => {
    if (!id) {
      return;
    }

    let isActive = true;

    const loadShot = async () => {
      setIsLoadingShot(true);
      try {
        const existingShot = await PortfolioService.getPortfolioItem(id);

        if (!isActive) {
          return;
        }

        if (!existingShot) {
          sonnerToast.error('Shot not found');
          navigate('/dashboard/portfolio');
          return;
        }

        originalSetShot(existingShot); // Use original to avoid tracking initial load
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error('[ShotEditorPage] Failed to load shot:', error);
        sonnerToast.error('Unable to load shot. Please try again.');
      } finally {
        if (isActive) {
          setIsLoadingShot(false);
        }
      }
    };

    loadShot();

    return () => {
      isActive = false;
    };
  }, [id, navigate, toast]);

  // Scroll behavior for header visibility
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY < lastScrollY || currentScrollY < 10) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleSave = async (isDraft: boolean = false) => {
    setIsSaving(true);
    setSavingAsDraft(isDraft);

    try {
      const newStatus = isDraft ? 'draft' : 'published';

      let savedShot: PortfolioItem;

      if (currentItemId) {
        // Update existing shot
        savedShot = await PortfolioService.updatePortfolioItem(currentItemId, {
          id: currentItemId,
          title: shot.title,
          description: shot.description,
          image_url: shot.image_url,
          images: shot.images,
          status: newStatus,
          is_featured: shot.is_featured,
          order: shot.order,
          slug: shot.slug,
        });
      } else {
        // Create new shot
        savedShot = await PortfolioService.createPortfolioItem({
          title: shot.title,
          description: shot.description,
          image_url: shot.image_url,
          images: shot.images,
          status: newStatus,
          is_featured: false,
          order: 0,
        });
      }

      setShot(savedShot);

      // Auto-create version when publishing
      if (savedShot.status === 'published' && savedShot.id && user?.id) {
        try {
          await createVersion(savedShot, undefined, 'auto_publish');
        } catch (error) {
          console.error('[ShotEditorPage] Failed to create auto-version:', error);
          // Don't block the save flow if version creation fails
        }
      }

      sonnerToast.success(isDraft ? 'Shot saved as draft' : 'Shot published successfully');

      if (!isDraft) {
        // Navigate back to portfolio after publishing
        navigate('/dashboard/portfolio');
      }
    } catch (error) {
      console.error('Error saving shot:', error);
      sonnerToast.error('Failed to save shot. Please try again.');
    } finally {
      setIsSaving(false);
      setSavingAsDraft(false);
    }
  };

  const handlePreview = () => {
    const snapshot: PortfolioItem = {
      ...shot,
      id: currentItemId || shot.id,
      images: shot.images
        ? shot.images.map((image) => ({ ...image }))
        : [],
    };

    setPreviewShot(snapshot);
    setIsPreviewModalOpen(true);
  };

  const handleCancel = () => {
    navigate('/dashboard/portfolio');
  };

  const handleShotChange = (updatedShot: PortfolioItem) => {
    setShot(updatedShot);
  };

  const handleDeleteClick = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!shot.id || !currentItemId) {
      toast({
        title: "Error",
        description: "Cannot delete a shot that hasn't been saved yet.",
        variant: "destructive",
      });
      return;
    }

    setIsDeleting(true);
    try {
      await PortfolioService.deletePortfolioItem(currentItemId);
      toast({
        title: "Success",
        description: "Shot deleted successfully.",
      });
      navigate("/dashboard/portfolio");
    } catch (error) {
      console.error("Error deleting shot:", error);
      toast({
        title: "Error",
        description: "Failed to delete shot. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleDuplicate = async () => {
    if (!shot.id || !currentItemId) {
      toast({
        title: "Error",
        description: "Cannot duplicate a shot that hasn't been saved yet.",
        variant: "destructive",
      });
      return;
    }

    setIsDuplicating(true);
    try {
      const duplicatedShot = await PortfolioService.duplicatePortfolioItem(currentItemId);
      toast({
        title: "Success",
        description: "Shot duplicated successfully.",
      });
      // Navigate to the duplicated shot editor
      navigate(`/dashboard/portfolio/shot/${duplicatedShot.id}`);
    } catch (error) {
      console.error("Error duplicating shot:", error);
      toast({
        title: "Error",
        description: "Failed to duplicate shot. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleSaveVersion = () => {
    // Check if shot has been saved (either shot.id or currentItemId should exist)
    if (!shot.id && !currentItemId) {
      toast({
        title: "Error",
        description: "Cannot save version of a shot that hasn't been saved yet.",
        variant: "destructive",
      });
      return;
    }
    setShowSaveVersionDialog(true);
  };

  const handleSaveVersionConfirm = async (label: string) => {
    if (!shot.id || !user?.id) return;

    try {
      await createVersion(shot, label || undefined, 'manual');
      sonnerToast.success('Version saved successfully');
      console.log('[ShotEditorPage] Version saved successfully');
    } catch (error) {
      console.error("[ShotEditorPage] Error saving version:", error);
      sonnerToast.error('Failed to save version. Please try again.');
    }
  };

  const handleRestoreVersionClick = (version: ShotVersion) => {
    setVersionToRestore(version);
    setShowRestoreVersionDialog(true);
  };

  const handleRestoreVersionConfirm = async (version: ShotVersion) => {
    try {
      // Restore the shot from the version snapshot
      const restoredShot = restoreFromSnapshot(shot, version.snapshot);

      // Clear undo/redo history since we're restoring
      clearHistory();

      // Update the shot state (bypasses history tracking)
      originalSetShot(restoredShot);

      toast({
        title: "Success",
        description: `Restored to ${version.label || `Version ${version.version_number}`}`,
      });
    } catch (error) {
      console.error("Error restoring version:", error);
      toast({
        title: "Error",
        description: "Failed to restore version. Please try again.",
        variant: "destructive",
      });
    }
  };

  const versionHistoryItems = useMemo(() => {
    return versions.map((version) => ({
      id: version.id,
      title: version.label || `Version ${version.version_number}`,
      timestamp: formatDistanceToNow(new Date(version.created_at), { addSuffix: true }),
      onRestore: () => handleRestoreVersionClick(version),
    }));
  }, [versions]);

  const isTitleValid = Boolean(shot.title?.trim());

  // Check if all required fields are filled for publishing
  const isFormValid = Boolean(
    shot.title?.trim() &&
    shot.description?.trim() &&
    (shot.image_url || (shot.images && shot.images.length > 0))
  );

  // Separate validation for different actions
  const canSaveDraft = Boolean(shot.title?.trim()) && !isLoadingShot;
  const canPreview = Boolean(shot.title?.trim() && shot.description?.trim()) && !isLoadingShot;
  const canPublish = isFormValid && !isLoadingShot;

  const handleClosePreview = () => {
    setIsPreviewModalOpen(false);
    setPreviewShot(null);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Authentication Required</h2>
          <p className="text-gray-600 mb-4">You must be logged in to edit shots.</p>
          <button
            onClick={() => navigate("/auth")}
            className="px-4 py-2 bg-gray-900 text-white rounded hover:bg-gray-800"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-transparent animate-fade-in flex flex-col">
      {/* CustomLayout with integrated shot header */}
      <CustomLayout
        shotMode={true}
        shot={shot}
        isSaving={isSaving}
        isTitleValid={isTitleValid}
        saveStatus={saveStatus}
        showAutosaveIndicator={true}
        onCancel={handleCancel}
        onSave={handleSave}
        onPreview={handlePreview}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={canUndo}
        canRedo={canRedo}
        onSaveVersion={handleSaveVersion}
        onDuplicate={handleDuplicate}
        onDelete={handleDeleteClick}
        versionHistoryItems={versionHistoryItems}
        isDuplicating={isDuplicating}
        savingAsDraft={savingAsDraft}
        canSaveDraft={canSaveDraft}
        canPreview={canPreview}
        canPublish={canPublish}
      >
        <div className="bg-[#EBECF0] flex flex-col items-stretch pt-24 pb-10 min-h-screen">
          <div className="max-w-7xl mx-auto w-full relative">
            <AddPortfolioItemForm
              onAddItem={handleShotChange}
              onCancel={handleCancel}
              editingItem={shot}
              userProfession={user.profession}
              userSkills={user.skills}
              initialImages={shot.images?.map(img => img.url) || []}
              initialData={shot}
              onItemChange={handleShotChange}
              onSaveStatusChange={(status) => {
                // Handle save status changes if needed
              }}
              hideButtons={true}
            />
            {isLoadingShot && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-transparent" />
                  Loading shot...
                </div>
              </div>
            )}
          </div>
        </div>
      </CustomLayout>

      {shot && (
        <PortfolioItemModal
          item={previewShot || shot}
          isOpen={isPreviewModalOpen}
          onClose={handleClosePreview}
          username={
            user?.username || user?.username_slug || user?.email || "designer"
          }
          username_slug={
            user?.username_slug || user?.username || user?.id || "designer"
          }
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Shot</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this shot? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Version Dialog */}
      <SaveVersionDialog
        open={showSaveVersionDialog}
        onOpenChange={setShowSaveVersionDialog}
        onSave={handleSaveVersionConfirm}
        isSaving={isSavingVersion}
      />

      {/* Restore Version Dialog */}
      {versionToRestore && (
        <RestoreVersionDialog
          open={showRestoreVersionDialog}
          onOpenChange={(open) => {
            setShowRestoreVersionDialog(open);
            if (!open) setVersionToRestore(null);
          }}
          onRestore={() => handleRestoreVersionConfirm(versionToRestore)}
          version={versionToRestore as any}
        />
      )}
    </div>
  );
}
